import { FloatingFormItem } from '@/components/_common';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import VehicleForm from '@/components/_common/_VehicleModal/VehicleForm';
import VehicleLicensePlate from '@/components/_common/_VehicleModal/VehicleLicensePlate';
import { parseAccessoryId } from '@/components/_common/_VehicleModal/getAccessoryId';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { useCreateCustomerVehicle } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import {
  CreateCustomerVehicleRequest,
  VehicleFormData,
  VehicleType,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { useCreateCustomer } from '@/core/domain/people/Customers/Customers.hooks';
import {
  Customer,
  PersonType,
} from '@/core/domain/people/Customers/Customers.types';
import { useImportedCpfCnpj } from '@/core/domain/people/ImportedCPFCNPJ/ImportedCPFCNPJ.hook';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Button, Col, Form, Input, message, Modal, Row } from 'antd';
import validateCpf from '@/utils/functions/validateCpf';
import { removeMask } from '@/utils/mask';
import { useEffect } from 'react';
import CpfCnpjInput from '@/components/_common/CpfCnpjInput';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import VehicleImage from '@/components/_common/_VehicleModal/VehicleImage';
import VehicleEngineDataForm from '@/components/_common/_VehicleModal/VehicleEngineDataForm';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';

interface Props {
  licensePlate?: string;
  opened: boolean;
  onClose: VoidFunction;
  createUser?: boolean;
}

export default function VisionLinkVehicle(props: Props) {
  const [form] = Form.useForm<
    VehicleFormData & {
      type?: VehicleType;
      customerName?: string;
      customerDocument?: string;
    }
  >();
  const formValues = Form.useWatch([], form);

  const fieldRequired = useDefaultEmptyFieldError();

  const queryChanel = useQueryChanel();

  const customerCreate = useCreateCustomer();
  const vehicelCreate = useCreateCustomerVehicle(
    !props.createUser ? formValues?.customerId : undefined,
  );

  const importCpfCnpj = useImportedCpfCnpj();

  const hadleCpfCnpj = async (value: string | undefined) => {
    const documentNumber = removeMask(value);
    const isValidCpf = validateCpf(documentNumber);
    const isValidCnpj = validateCnpj(documentNumber);

    if (isValidCpf || isValidCnpj) {
      const data = await importCpfCnpj.mutateAsync(documentNumber);

      form.setFieldsValue({ customerName: data.name });
    }
  };

  useEffect(() => {
    if (props.opened && props.licensePlate) {
      form.setFieldsValue({
        licensePlate: props.licensePlate,
      });
    }
  }, [props]);

  const handleClose = () => {
    form.resetFields();
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let customerResponse = {} as Customer;

      let personType = PersonType.Natural;

      const customerDocument = removeMask(values.customerDocument);

      if ((customerDocument?.length || 0) > 11) {
        personType = PersonType.Legal;
      }

      if (props.createUser && values.customerName) {
        customerResponse = await customerCreate.mutateAsync({
          name: values.customerName,
          active: true,
          personType,
          cpfCnpj: customerDocument,
        });
      }

      const accessories = values.accessories?.map((accessory) =>
        parseAccessoryId(accessory),
      );

      let image = values.image;

      if (!!values.imageUpload) {
        const result = await uploadArchive({
          file: [values.imageUpload as Blob],
          isPublic: false,
        });

        image = result.filePath;
      }

      const data: CreateCustomerVehicleRequest = {
        model: {
          id: values.modelId,
          brand: { id: values.brandId, image: values.model?.brand.image },
        },
        fuel: { id: values.fuelId },
        color: { id: values.colorId },
        licensePlate: values.licensePlate,
        yearFabrication: Number(values.yearFabrication),
        yearModel: Number(values.yearModel),
        chassi: values.chassi,
        fleet: values.fleet,
        km: values.km,
        renavam: values.renavam,
        type: values?.type,
        active: true,
        accessories: accessories || [],
        customerId: props.createUser ? customerResponse?.id : undefined,
        image,
        cylinders: values.cylinders,
        engineCapacity: values.engineCapacity,
        engineName: values.engineName,
        engineTechnicalCode: values.engineTechnicalCode,
        horsePower: values.horsePower,
        release: values.release,
        valves: values.valves,
        line: values.line,
        airIntake: values.airIntake,
        rearBrake: values.rearBrake,
        frontBrake: values.frontBrake,
        brakeSystem: values.brakeSystem,
        steeringSystem: values.steeringSystem,
        tractionSystem: values.tractionSystem,
        injectionSystem: values.injectionSystem,
        transmissionType: values.transmissionType,
        engineConfiguration: values.engineConfiguration,
      };

      await vehicelCreate.mutateAsync(data);

      queryChanel({
        invalidateQueries: [
          ['customer/filter/reduced', { licensePlate: props.licensePlate }],
        ],
      });

      handleClose();
    } catch (error) {
      const err = error as { errorFields?: Record<string, string> };

      if (err?.errorFields) {
        message.warn('Valide os campos');
      }
    }
  };

  return (
    <Modal
      open={props.opened}
      onCancel={handleClose}
      destroyOnClose
      title="Vincular veículo a um cliente"
      confirmLoading={vehicelCreate.isLoading}
      style={{ top: 24, position: 'relative' }}
      footer={
        <SpaceForButton justifyContent="flex-end">
          <Button block onClick={handleClose}>
            Cancelar
          </Button>

          <Button block type="primary" onClick={handleSubmit}>
            Vincular veículo
          </Button>
        </SpaceForButton>
      }
    >
      {customerCreate.isLoading && (
        <ModalLoadingOverlay messages={['Criando registro do cliente...']} />
      )}

      {vehicelCreate.isLoading && (
        <ModalLoadingOverlay messages={['Vinculando veículo ao cliente...']} />
      )}

      <Form form={form}>
        <div style={{ marginBottom: 24 }}>
          {!props.createUser && (
            <>
              <strong style={{ display: 'inline-block', marginBottom: 8 }}>
                Selecione um cliente
              </strong>

              <FloatingFormItem
                name="customerId"
                label="Cliente"
                rules={[fieldRequired]}
              >
                <CustomersSelectField disabled={vehicelCreate.isLoading} />
              </FloatingFormItem>
            </>
          )}

          {!!props.createUser && (
            <>
              <strong style={{ display: 'inline-block', marginBottom: 8 }}>
                Dados do cliente
              </strong>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <FloatingFormItem
                    name="customerName"
                    label="Nome do cliente"
                    rules={[fieldRequired]}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>

                <Col span={24}>
                  <FloatingFormItem
                    name="customerDocument"
                    label="CPF/CNPJ"
                    required={false}
                    rules={[
                      {
                        type: 'string',
                        validator(_, value) {
                          return new Promise((res, rej) => {
                            const documentNumber = removeMask(value) as string;
                            const isValidCpf = validateCpf(documentNumber);
                            const isValidCnpj = validateCnpj(documentNumber);

                            if (
                              (documentNumber.length === 11 && isValidCpf) ||
                              (documentNumber.length === 14 && isValidCnpj)
                            ) {
                              return res(value);
                            }

                            if (documentNumber.length <= 11) {
                              return rej('CPF inválido');
                            }
                            return rej('CNPJ inválido');
                          });
                        },
                      },
                    ]}
                    getValueFromEvent={(value) => {
                      hadleCpfCnpj(value);
                      return value;
                    }}
                  >
                    <CpfCnpjInput />
                  </FloatingFormItem>
                </Col>
              </Row>
            </>
          )}
        </div>

        <strong style={{ display: 'inline-block', marginBottom: 8 }}>
          Dados do veículo
        </strong>
        <div style={{ marginBottom: 16 }}>
          <VehicleLicensePlate consultLicensePlate />
        </div>

        <Row
          gutter={[12, 12]}
          align="middle"
          className="vehicle-form-container"
        >
          <Col span={24} md={16}>
            <VehicleForm disabledFields={vehicelCreate.isLoading} />
          </Col>

          <Col span={24} md={8}>
            <VehicleImage disabledFields={vehicelCreate.isLoading} />
          </Col>

          <Col span={24}>
            <VehicleEngineDataForm disabledFields={vehicelCreate.isLoading} />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
